import {
  MultiSelect,
  MultiSelectProps,
  Select,
  SelectProps,
} from '@mantine/core';

import { useGetManyDrugsQuery } from '@api/drugs.api';

import useStateWithDebounce from '@hooks/useStateWithDebouce';

type DrugsSelectorProps =
  | (Omit<
      SelectProps,
      'data' | 'searchable' | 'nothingFoundMessage' | 'placeholder'
    > & {
      multiple?: false;
    })
  | (Omit<
      MultiSelectProps,
      'data' | 'searchable' | 'nothingFoundMessage' | 'placeholder'
    > & {
      multiple: true;
    });

export default function DrugsSelector(props: DrugsSelectorProps) {
  // ==========================================================================
  // State
  // ==========================================================================
  const [searchQuery, setSearchQuery, debouncedSearchQuery] =
    useStateWithDebounce('');

  // ==========================================================================
  // Api
  // ==========================================================================
  const { data = [], isLoading } = useGetManyDrugsQuery(
    {
      sortBy: 'name',
      sortOrder: 'asc',
      nameSearch: debouncedSearchQuery,
    },
    { skip: debouncedSearchQuery.trim() === '' },
  );

  // ==========================================================================
  // Render
  // ==========================================================================
  return props.multiple ? (
    <MultiSelect
      {...props}
      searchable
      searchValue={searchQuery}
      onSearchChange={setSearchQuery}
      nothingFoundMessage={
        debouncedSearchQuery.trim() === ''
          ? 'Inizia a digitare il nome del farmaco per cercare'
          : 'Nessun farmaco trovato'
      }
      placeholder={
        props.value?.length || 0 > 0 ? undefined : 'Seleziona i farmaci'
      }
      data={data.map((drug) => ({
        value: drug.aic,
        label: drug.name,
      }))}
      disabled={isLoading || props.disabled}
    />
  ) : (
    <Select
      {...props}
      searchable
      searchValue={searchQuery}
      onSearchChange={setSearchQuery}
      nothingFoundMessage={
        debouncedSearchQuery.trim() === ''
          ? 'Inizia a digitare il nome del farmaco per cercare'
          : 'Nessun farmaco trovato'
      }
      placeholder="Ricerca il farmaco"
      data={data.map((drug) => ({
        value: drug.aic,
        label: drug.name,
      }))}
      disabled={isLoading || props.disabled}
    />
  );
}
